import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import useNavRowLabels from './useNavRowLabels'
import ExperienceNavigationDrawer from './ExperienceNavigationDrawer'
import ExperienceNavSoleRowHandle from './ExperienceNavSoleRowHandle'
import ExperienceNavStepButtons from './ExperienceNavStepButtons'
import navigationItemPropType, { navigationParentPropType } from './navigationItemPropType'
import { getSoleNavRow } from './navigationModel'
import { EDGE_TAB_PLACEMENT, TOP_CORNER_PLACEMENT } from './experienceNavSurface.styles'

/**
 * What a migrated experience actually gets: a card holding every way out of
 * where the visitor is, the handle that opens it, and - when the card is
 * indexing a section the visitor sits inside - a stepper that walks that same
 * list without opening anything.
 *
 * Where the card would hold a single row there is nothing to choose from, so
 * the handle wears that row instead - same place, same surface, one tap.
 *
 * Rendered by ExperienceNavControls; never mount it directly from an experience.
 */
export default function SharedNavigationSurface({
  isStation,
  headerVisible,
  registration = null,
  showBack = false,
  showHome = false,
  showKidsReturn = false,
  showLeaveKiosk = false,
  parentItem = null,
  sectionLabel = null,
  navItems = [],
  stepRing = null,
  onBack,
  onSelectParent,
  onSelectNavItem,
  onHome,
  onKidsReturn,
  onLeaveKiosk,
}) {
  const { t } = useTranslation()
  const { navigationLabel, backLabel } = useNavRowLabels()
  // Deliberate exception to "layout follows width, not mode" (CLAUDE.md,
  // Responsive Layout): the edge tab is kiosk chrome, not a layout. A docked
  // kiosk has no global header, no browser chrome and is driven by touch from
  // standing height, so its one control sits flush against the side where a
  // hand rests; a browser window of the same width still gets the corner
  // button beside the global header. Mode answers "what is this screen", and
  // that is the question here.
  const triggerPlacement = isStation ? EDGE_TAB_PLACEMENT : TOP_CORNER_PLACEMENT
  const soleRow = getSoleNavRow({ parentItem, showBack, navItems, showHome, showKidsReturn, showLeaveKiosk })

  // Every handler the card's rows use, so a handle wearing one of them is
  // that row exactly rather than a paraphrase of it. (The labels both read
  // from useNavRowLabels.)
  const rowProps = {
    backAriaLabel: registration?.ariaLabel ?? backLabel,
    onBack,
    onSelectParent,
    onSelectNavItem,
    onHome,
    onKidsReturn,
    onLeaveKiosk,
  }

  return (
    <>
      {soleRow ? (
        <ExperienceNavSoleRowHandle
          row={soleRow}
          triggerPlacement={triggerPlacement}
          headerVisible={headerVisible}
          {...rowProps}
        />
      ) : (
        <ExperienceNavigationDrawer
          triggerPlacement={triggerPlacement}
          headerVisible={headerVisible}
          showBack={showBack}
          showHome={showHome}
          showKidsReturn={showKidsReturn}
          showLeaveKiosk={showLeaveKiosk}
          parentItem={parentItem}
          sectionLabel={sectionLabel}
          navItems={navItems}
          {...rowProps}
        />
      )}
      {stepRing && (
        <ExperienceNavStepButtons
          triggerPlacement={triggerPlacement}
          headerVisible={headerVisible}
          // The section names the ring where there is one; elsewhere the ring
          // IS the experience's top level, which navigationLabel names.
          groupLabel={sectionLabel ?? navigationLabel}
          previousItem={stepRing.previous}
          nextItem={stepRing.next}
          previousLabel={t('discovery.nav.previous-destination', 'Previous: {label}', {
            label: stepRing.previous.label,
          })}
          nextLabel={t('discovery.nav.next-destination', 'Next: {label}', { label: stepRing.next.label })}
          onSelect={onSelectNavItem}
        />
      )}
    </>
  )
}

SharedNavigationSurface.propTypes = {
  /** Places the handle: flush against the side on a kiosk, top-start corner on the web. */
  isStation: PropTypes.bool.isRequired,
  headerVisible: PropTypes.bool.isRequired,
  /** Names the Back row; see buildNavigationIndex for whether that row renders. */
  registration: PropTypes.shape({ ariaLabel: PropTypes.string }),
  /** The registration renders as its own Back row (it isn't already the level above). */
  showBack: PropTypes.bool,
  showHome: PropTypes.bool,
  showKidsReturn: PropTypes.bool,
  showLeaveKiosk: PropTypes.bool,
  parentItem: navigationParentPropType,
  sectionLabel: PropTypes.string,
  navItems: PropTypes.arrayOf(navigationItemPropType),
  /** The rows either side of the current one; absent where stepping means nothing. */
  stepRing: PropTypes.shape({
    previous: navigationItemPropType.isRequired,
    next: navigationItemPropType.isRequired,
  }),
  onBack: PropTypes.func.isRequired,
  onSelectParent: PropTypes.func.isRequired,
  onSelectNavItem: PropTypes.func.isRequired,
  onHome: PropTypes.func.isRequired,
  onKidsReturn: PropTypes.func.isRequired,
  onLeaveKiosk: PropTypes.func.isRequired,
}
